import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import {
  useClientsAddPointAlias,
  useClientsCreateDeliveryPoint,
  useClientsDeletePointAlias,
  useClientsUpdateDeliveryPoint,
} from '@/api/generated/endpoints/clients/clients';
import type {
  ClientDetail,
  DeliveryPointDetail,
  DeliveryPointUpdate,
  PointAliasCreate,
} from '@/api/generated/model';
import { useHasRole } from '@/auth/useHasRole';
import { kppRules, POINT_FIELD_BY_CODE } from '@/pages/catalog/clientForm';
import { problemMessage } from '@/i18n/problem';
import { applyProblem, problemText } from '@/lib/problemForm';
import { blankToNull } from '@/lib/text';
import { invalidatePath } from '@/lib/queryCache';
import { useIsMobile } from '@/lib/useIsMobile';

/** Точки доставки клиента: РЦ и магазины; алиасы — как грузополучатель записан в УПД. */
export function DeliveryPointsTab({ client }: { client: ClientDetail }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const canCreate = useHasRole('storekeeper');
  const canEdit = useHasRole('manager');
  const [editing, setEditing] = useState<DeliveryPointDetail | 'new' | null>(null);

  const columns: TableColumnsType<DeliveryPointDetail> = [
    {
      key: 'name',
      title: t('points.colName'),
      dataIndex: 'name',
      render: (name: string, p) => (
        <Space>
          {name}
          {!p.is_active && <Tag>{t('common.archived')}</Tag>}
        </Space>
      ),
    },
    {
      key: 'address',
      title: t('points.colAddress'),
      dataIndex: 'address',
      render: (a: string | null) => a ?? '—',
    },
    {
      key: 'kpp',
      title: t('clients.kpp'),
      dataIndex: 'kpp',
      width: 110,
      render: (k: string | null) => k ?? '—',
    },
    {
      key: 'aliases',
      title: t('points.colAliases'),
      width: 90,
      render: (_: unknown, p) => p.aliases.length,
    },
    {
      key: 'actions',
      width: 50,
      render: (_: unknown, p) =>
        canEdit && (
          <Button
            type="text"
            icon={<EditOutlined />}
            aria-label={t('common.edit')}
            onClick={() => {
              setEditing(p);
            }}
          />
        ),
    },
  ];

  // Телефон (T-177): точка — одной ячейкой: название, адрес, КПП, псевдонимы; кнопки — отдельной узкой колонкой.
  const mobileColumns: TableColumnsType<DeliveryPointDetail> = [
    {
      key: 'name',
      title: t('points.colName'),
      render: (_: unknown, p) => (
        <Space direction="vertical" size={2}>
          <Space size={4} wrap>
            {p.name}
            {!p.is_active && <Tag>{t('common.archived')}</Tag>}
          </Space>
          {p.address && <Typography.Text type="secondary">{p.address}</Typography.Text>}
          <Typography.Text type="secondary">
            {p.kpp ? `${t('clients.kpp')} ${p.kpp} · ` : ''}
            {t('points.colAliases')}: {p.aliases.length}
          </Typography.Text>
        </Space>
      ),
    },
    ...columns.filter((c) => c.key === 'actions'),
  ];

  return (
    <>
      {canCreate && (
        <Button
          icon={<PlusOutlined />}
          style={{ marginBottom: 12 }}
          onClick={() => {
            setEditing('new');
          }}
        >
          {t('points.add')}
        </Button>
      )}
      <Table<DeliveryPointDetail>
        rowKey="id"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={client.delivery_points}
        pagination={false}
        expandable={{
          expandedRowRender: (p) => <PointAliases clientId={client.id} point={p} />,
        }}
        locale={{ emptyText: t('points.empty') }}
      />
      <PointModal
        clientId={client.id}
        point={editing}
        onClose={() => {
          setEditing(null);
        }}
      />
    </>
  );
}

function PointModal({
  clientId,
  point,
  onClose,
}: {
  clientId: number;
  point: DeliveryPointDetail | 'new' | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<DeliveryPointUpdate>();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const isNew = point === 'new';
  const handlers = {
    onSuccess: async () => {
      await invalidatePath(queryClient, '/api/v1/clients');
      onClose();
    },
    onError: (e: unknown) => {
      if (!applyProblem(form, e, POINT_FIELD_BY_CODE)) setError(problemText(e));
    },
  };
  const create = useClientsCreateDeliveryPoint({ mutation: handlers });
  const update = useClientsUpdateDeliveryPoint({ mutation: handlers });

  return (
    <Modal
      title={isNew ? t('points.new') : t('points.edit')}
      open={point !== null}
      onCancel={() => {
        setError(null);
        onClose();
      }}
      okText={t('common.save')}
      okButtonProps={{ loading: create.isPending || update.isPending }}
      onOk={() => {
        form.submit();
      }}
      destroyOnHidden
    >
      <Form<DeliveryPointUpdate>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={
          point && point !== 'new'
            ? {
                name: point.name,
                address: point.address,
                kpp: point.kpp,
                is_active: point.is_active,
              }
            : { is_active: true }
        }
        onFinish={(v) => {
          setError(null);
          const address = blankToNull(v.address);
          const kpp = blankToNull(v.kpp);
          if (isNew) {
            create.mutate({ clientId, data: { name: v.name ?? '', address, kpp } });
          } else if (point) {
            const data = { ...v, address, kpp };
            update.mutate({ clientId, dpId: point.id, data });
          }
        }}
      >
        <Form.Item
          name="name"
          label={t('points.name')}
          rules={[{ required: true, whitespace: true, message: t('points.nameRequired') }]}
        >
          <Input autoFocus maxLength={300} placeholder="РЦ Вешки" />
        </Form.Item>
        <Form.Item name="address" label={t('points.address')}>
          <Input.TextArea maxLength={500} autoSize={{ minRows: 2 }} />
        </Form.Item>
        <Form.Item
          name="kpp"
          label={t('points.kpp')}
          rules={kppRules(t)}
          extra={t('points.kppHint')}
        >
          <Input maxLength={9} />
        </Form.Item>
        {!isNew && (
          <Form.Item name="is_active" label={t('common.activeF')} valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}

function PointAliases({ clientId, point }: { clientId: number; point: DeliveryPointDetail }) {
  const { t } = useTranslation();
  const canAdd = useHasRole('storekeeper');
  const canDelete = useHasRole('manager');
  const [form] = Form.useForm<PointAliasCreate>();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const refresh = () => invalidatePath(queryClient, '/api/v1/clients');
  const add = useClientsAddPointAlias({
    mutation: {
      onSuccess: async () => {
        form.resetFields();
        await refresh();
      },
      onError: (e) => {
        if (!applyProblem(form, e, { alias_conflict: 'alias' }))
          void message.error(problemMessage(e));
      },
    },
  });
  const remove = useClientsDeletePointAlias({
    mutation: { onSuccess: refresh, onError: (e) => void message.error(problemText(e)) },
  });

  return (
    <div style={{ maxWidth: 720 }}>
      <Typography.Text type="secondary">{t('points.aliasesHint')}</Typography.Text>
      <List
        size="small"
        dataSource={point.aliases}
        locale={{ emptyText: t('points.aliasesEmpty') }}
        renderItem={(a) => (
          <List.Item
            actions={
              canDelete
                ? [
                    <Popconfirm
                      key="delete"
                      title={t('points.aliasDeleteConfirm')}
                      onConfirm={() => {
                        remove.mutate({ clientId, dpId: point.id, aliasId: a.id });
                      }}
                    >
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]
                : []
            }
          >
            {a.alias}
          </List.Item>
        )}
      />
      {canAdd && (
        <Form<PointAliasCreate>
          form={form}
          layout="inline"
          onFinish={(v) => {
            add.mutate({ clientId, dpId: point.id, data: v });
          }}
        >
          <Form.Item
            name="alias"
            style={{ flex: 1 }}
            rules={[{ required: true, whitespace: true, message: t('points.aliasRequired') }]}
          >
            <Input placeholder={t('points.aliasPlaceholder')} maxLength={1000} />
          </Form.Item>
          <Button htmlType="submit" loading={add.isPending}>
            {t('common.add')}
          </Button>
        </Form>
      )}
    </div>
  );
}
